import { Build, Step } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";
import { queryOptions, UseQueryOptions } from "@tanstack/react-query";
import { FilterByOption } from "~/components/build/Show/lib/useFilterStore";
import { setStepQueryData } from "~/components/build/Show/lib/queries/step";
import {
  compareSortKeys,
  computeStepSortKey,
} from "~/components/build/Show/lib/stores/buildStore/sortKeys";

/**
 * All steps query
 */
export const allStepsQueryKey = (buildId: Build["id"]) => {
  return ["build", buildId, "steps", "all"];
};

export const allStepsQueryOptions = (
  build: Build,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: allStepsQueryKey(build.id),
    queryFn: ({ client: queryClient, signal }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        stepCount: build.statistics?.stepsCount,
        signal,
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build.id, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Top-level steps query
 */

export const topLevelStepsQueryKey = (buildId: Build["id"]) => {
  return ["build", buildId, "steps", "depth:top"];
};

export const topLevelStepsQueryOptions = (
  build: Build,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: topLevelStepsQueryKey(build.id),
    queryFn: ({ client: queryClient, signal }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { depth: "top" },
        signal,
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build.id, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Children steps query (for group steps)
 */
export const childrenStepsQueryKey = (buildId: Build["id"], stepUuid: Step["uuid"]) => {
  return ["build", buildId, "steps", stepUuid, "children"];
};

export const childrenStepsQueryOptions = (
  build: Build,
  stepUuid: Step["uuid"],
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: childrenStepsQueryKey(build.id, stepUuid),
    queryFn: ({ client: queryClient, signal }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { groupUuid: stepUuid },
        signal,
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build.id, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Steps by state
 */
export const stepsByStateQueryKey = (
  buildId: Build["id"],
  state: FilterByOption,
  { excludeGroupSteps = false }: { excludeGroupSteps?: boolean } = {},
) => {
  return [
    "build",
    buildId,
    "steps",
    `filter:${state}`,
    ...(excludeGroupSteps ? ["excludeGroups"] : []),
  ];
};

export const stepsByStateQueryOptions = (
  build: Build,
  state: FilterByOption,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
  { excludeGroupSteps = false }: { excludeGroupSteps?: boolean } = {},
) => {
  return queryOptions({
    queryKey: stepsByStateQueryKey(build.id, state, { excludeGroupSteps }),
    queryFn: ({ client: queryClient, signal }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { state, ...(excludeGroupSteps ? { excludeGroupSteps: true } : {}) },
        signal,
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build.id, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Fetches the steps for a build optionally filtered by state.
 *
 * When the BuildParallelStepFetching feature flag is enabled and there are
 * more than 250 steps (and no filters), the fetch is split into parallel
 * partitioned requests using hash-based partitioning on the backend.
 */

const PARALLEL_STEP_THRESHOLD = 250;
const MAX_PARTITIONS = 8;

type FetchBuildStepsParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  stepCount?: number;
  filters?: {
    state?: FilterByOption;
    excludeGroupSteps?: boolean;
    depth?: "top" | "all";
    groupUuid?: Step["groupUuid"];
  };
  signal: AbortSignal;
};

async function fetchBuildSteps({
  buildDataBasePath,
  stepCount,
  filters,
  signal,
}: FetchBuildStepsParams): Promise<Step[]> {
  // Parallel partitioned fetch for large builds (no filters)
  if (
    !filters &&
    stepCount &&
    stepCount > PARALLEL_STEP_THRESHOLD &&
    Features.BuildParallelStepFetching
  ) {
    const partitions = Math.min(Math.ceil(stepCount / PARALLEL_STEP_THRESHOLD), MAX_PARTITIONS);
    const requests = Array.from({ length: partitions }, (_partition, partitionIndex) => {
      const url = new URL(`${buildDataBasePath}/steps`, window.location.origin);
      url.searchParams.append("partition", String(partitionIndex));
      url.searchParams.append("partitions", String(partitions));
      return fetcher<Step[]>(url, true, { signal });
    });
    try {
      return (await Promise.all(requests))
        .flat()
        .sort((stepA, stepB) =>
          compareSortKeys(computeStepSortKey(stepA), computeStepSortKey(stepB)),
        );
    } catch (error: unknown) {
      if (!isStalePartitionRejection(error)) {
        throw error;
      }

      const url = new URL(`${buildDataBasePath}/steps`, window.location.origin);
      return fetcher(url, true, { signal });
    }
  }

  const url = new URL(`${buildDataBasePath}/steps`, window.location.origin);

  // Note: The order of the parameters matters since rails will automatically order search params alphabetically.
  // This means in order to prefetch this query we need to ensure that the parameters are in the same order.

  if (filters && filters.depth !== undefined) {
    url.searchParams.append("depth", String(filters.depth));
  }

  if (filters && filters.excludeGroupSteps !== undefined) {
    url.searchParams.append("exclude_group_steps", String(filters.excludeGroupSteps));
  }

  if (filters && filters.groupUuid !== undefined) {
    url.searchParams.append("group_uuid", filters.groupUuid);
  }

  if (filters?.state) {
    url.searchParams.append("state", filters.state);
  }

  return fetcher(url, true, { signal });
}

function isStalePartitionRejection(error: unknown): boolean {
  return error instanceof Error && "status" in error && error.status === 400;
}
